import * as React from 'react';
import {useJsonForms, withJsonFormsControlProps} from '@jsonforms/react';
import Checkbox from './Checkbox';
import {SchemaUtils} from "../../util/SchemaUtils";
import {ControlValueFactory} from "../../factory/ControlValueFactory";
import {Component} from "../../enum/Component";
import { useMemo, useCallback } from 'react';

interface CheckboxControlProps {
    data: any;
    handleChange(path: string, value: string[]): void; // Changed to string[] for checkbox values
    path: string;
    errors: any;
    uischema: any;
    schema: any;
    rootSchema: any;
    appValidated?: boolean;
}

const CheckboxControl = ({ errors, uischema, schema, handleChange, path, rootSchema, data, appValidated }: CheckboxControlProps) => {

    const jsonForms = useJsonForms();

    // Memoize expensive computations
    const enabled = useMemo(() =>
            SchemaUtils.isComponentEnabled(uischema, jsonForms.core!.data),
        [uischema, jsonForms.core!.data]
    );

    const visible = useMemo(() =>
            SchemaUtils.isComponentVisible(uischema, jsonForms.core!.data),
        [uischema, jsonForms.core!.data]
    );

    const required = useMemo(() =>
            SchemaUtils.isRequired(path, rootSchema),
        [path, rootSchema]
    );

    const value = useMemo(() =>
            ControlValueFactory.getControlValue(Component.Checkbox, data, uischema.scope, jsonForms, enabled && visible),
        [Component.Checkbox, data, uischema.scope, jsonForms, enabled, visible]
    );

    // Memoize the change handler - Note: Checkbox should handle array toggling internally
    const handleCheckboxChange = useCallback((newValue: string[]) => {
        handleChange(path, newValue);
    }, [handleChange, path]);

    // Safely access enum values with fallback
    const enumValues = useMemo(() =>
            schema.items?.enum || [],
        [schema.items?.enum]
    );

    // Memoize the Checkbox component props to prevent unnecessary re-renders
    const checkboxProps = useMemo(() => ({
        error: errors,
        value: Array.isArray(value) ? value : [], // Ensure array type
        required: required,
        data: enumValues,
        label: uischema.label,
        description: schema.description,
        onChange: handleCheckboxChange,
        enabled: enabled,
        visible: visible,
        appValidated: appValidated
    }), [
        errors,
        value,
        required,
        enumValues,
        uischema.label,
        schema.description,
        handleCheckboxChange,
        enabled,
        visible,
        appValidated
    ]);

    return <Checkbox {...checkboxProps} />;
};

export default React.memo(withJsonFormsControlProps(CheckboxControl));
